import React, { useState, ChangeEvent } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUpload, FaPlus, FaArrowLeft, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import api from "../api/axiosInstance";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const PlusButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 100px;
    background-color: #f56607;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 2em;
    cursor: pointer;

    &:hover {
        background-color: #f58802;
    }
`;

const UploadContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 400px;
    max-width: 400px;
    margin: 20px 0;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow-y: auto;
`;

const BackButton = styled.button`
    align-self: flex-start;
    background-color: transparent;
    border: none;
    color: #f56607;
    cursor: pointer;
    font-size: 1.5em;

    &:hover {
        color: #f58802;
    }
`;

const Input = styled.input`
    margin-top: 20px;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #ddd;
`;

const TextArea = styled.textarea`
    margin: 10px 0;
    padding: 10px;
    width: 100%;
    height: 150px;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #ddd;
`;

const Select = styled.select`
    margin: 10px 0;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #ddd;
`;

const FileInputLabel = styled.label`
    display: flex;
    align-items: center;
    margin: 20px 0;
    padding: 10px 20px;
    background-color: #f56607;
    color: white;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #f58802;
    }
`;

const FileInput = styled.input`
    display: none;
`;

const FileName = styled.div`
    margin-top: 10px;
    font-size: 1em;
    color: #555;
`;

const SubmitButton = styled.button`
    margin: 10px 0;
    padding: 10px 20px;
    background-color: #f56607;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #f58802;
    }
`;

const ResetButton = styled.button`
    margin: 10px 0;
    padding: 10px 20px;
    background-color: #ddd;
    color: #333;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #ccc;
    }
`;

const spinnerAnimation = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const SpinnerIcon = styled(FaSpinner)`
    animation: ${spinnerAnimation} 2s linear infinite;
`;

const SuccessIcon = styled(FaCheckCircle)`
    color: green;
    font-size: 2em;
    margin-top: 20px;
`;

const LongResponseMessage = styled.div`
    color: red;
    margin-top: 10px;
    font-size: 1em;
    text-align: center;
`;

const SurveyContainer = styled.div`
    width: 100%;
    margin: 10px 0;
`;

const AddOptionButton = styled.button`
    margin-top: 10px;
    padding: 5px 10px;
    background-color: #f56607;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #f58802;
    }
`;

interface SurveyPostFormProps {
    options: string[];
    onOptionsChange: (options: string[]) => void;
}

const SurveyPostForm: React.FC<SurveyPostFormProps> = ({ options, onOptionsChange }) => {
    const addOption = () => {
        onOptionsChange([...options, '']);
    };

    const updateOption = (index: number, value: string) => {
        const newOptions = [...options];
        newOptions[index] = value;
        onOptionsChange(newOptions);
    };

    return (
        <SurveyContainer>
            {options.map((opt, index) => (
                <Input
                    key={index}
                    type="text"
                    placeholder={`Možnosť ${index + 1}`}
                    value={opt}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateOption(index, e.target.value)}
                />
            ))}
            <AddOptionButton onClick={addOption}>Pridať možnosť</AddOptionButton>
        </SurveyContainer>
    );
};

type PostType =
    | 'POST'
    | 'ANKETA'
    | 'KRATKODOBY_TIP'
    | 'DLHODOBY_TIP'
    | 'PODCAST';

interface UploadSectionProps {
    onUploadSuccess: () => void; // callback for successful upload
}

const UploadSection: React.FC<UploadSectionProps> = ({ onUploadSuccess }) => {
    const [showUpload, setShowUpload] = useState(false);
    const [selectedType, setSelectedType] = useState<PostType>('POST');
    const [files, setFiles] = useState<File[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [heading, setHeading] = useState('');
    const [description, setDescription] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [longResponse, setLongResponse] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processingFailed, setProcessingFailed] = useState(false);
    const [surveyOptions, setSurveyOptions] = useState<string[]>(['']);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            setFiles(selectedFiles);
            setFileNames(selectedFiles.map(file => file.name));
        }
    };

    const handleUploadFiles = async (postId: number) => {
        if (files.length === 0) {
            return;
        }

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        await api.post(`/posts/native/upload-files?postId=${postId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 1200000,
        });
    };

    // Video/audio transcode to HLS on the server asynchronously; poll the post until it's ready.
    const waitForMediaReady = async (postId: number): Promise<'READY' | 'FAILED' | 'TIMEOUT'> => {
        const deadline = Date.now() + 5 * 60 * 1000; // allow large videos up to 5 minutes
        while (Date.now() < deadline) {
            try {
                const r = await api.get(`/posts/${postId}`);
                const status = r.data?.mediaStatus;
                if (status === 'READY' || status == null) return 'READY';
                if (status === 'FAILED') return 'FAILED';
            } catch {
                // transient error — keep polling
            }
            await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        return 'TIMEOUT';
    };

    const notifyAndSucceed = async (postId: number) => {
        await api.post('/posts/sendNotification', null, {
            params: { postId },
            headers: { 'Content-Type': 'application/json' },
        });
        setUploadSuccess(true);
        onUploadSuccess();
    };

    const handleSubmit = async () => {
        setIsUploading(true);
        setUploadSuccess(false);
        setLongResponse(false);
        setProcessing(false);
        setProcessingFailed(false);

        const postDto: any = {
            heading,
            description,
            type: selectedType,
            surveyOptions: selectedType === 'ANKETA' ? surveyOptions.map(opt => ({ optionText: opt })) : null,
        };

        try {
            const response = await api.post('/posts/native', postDto, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 1200000,
            });

            if (response.status === 200 || response.status === 201) {
                const postId: number = response.data.id;

                if (selectedType !== 'ANKETA' && files.length > 0) {
                    await handleUploadFiles(postId);

                    // Let the SERVER decide whether it's transcoding media (don't guess from file.type,
                    // which is empty/unreliable for some containers).
                    let initialStatus: string | null = null;
                    try {
                        const r = await api.get(`/posts/${postId}`);
                        initialStatus = r.data?.mediaStatus ?? null;
                    } catch {
                        // treat as non-processing
                    }

                    if (initialStatus === 'PROCESSING') {
                        // Media transcodes asynchronously; the server notifies subscribers on READY.
                        setIsUploading(false);
                        setProcessing(true);
                        const outcome = await waitForMediaReady(postId);
                        setProcessing(false);
                        if (outcome === 'FAILED') {
                            setProcessingFailed(true);
                            return;
                        }
                        if (outcome === 'TIMEOUT') {
                            setLongResponse(true);
                            return;
                        }
                        setUploadSuccess(true);
                        onUploadSuccess();
                    } else {
                        // Ready immediately (image/status) — the client sends the notification.
                        await notifyAndSucceed(postId);
                    }
                } else {
                    await notifyAndSucceed(postId);
                }
            }
        } catch (error) {
            console.error('Chyba pri nahrávaní:', error);
            setProcessingFailed(true); // surface upload/create failures instead of silently going idle
        } finally {
            setIsUploading(false);
        }
    };

    const resetForm = () => {
        setHeading('');
        setDescription('');
        setFiles([]);
        setFileNames([]);
        setSurveyOptions(['']);
        setSelectedType('POST');
        setUploadSuccess(false);
        setLongResponse(false);
        setProcessing(false);
        setProcessingFailed(false);
    };

    return (
        <Container>
            {!showUpload ? (
                <PlusButton onClick={() => setShowUpload(true)}>
                    <FaPlus />
                </PlusButton>
            ) : (
                <UploadContainer>
                    <BackButton onClick={() => setShowUpload(false)}>
                        <FaArrowLeft />
                    </BackButton>
                    <h2>Pridať príspevok</h2>
                    <Select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value as PostType)}
                    >
                        <option value="POST">Post</option>
                        <option value="ANKETA">Anketa</option>
                        <option value="KRATKODOBY_TIP">Krátkodobý Tip</option>
                        <option value="DLHODOBY_TIP">Dlhodobý Tip</option>
                        <option value="PODCAST">Podcast</option>
                    </Select>
                    <Input
                        type="text"
                        placeholder="Zadajte názov"
                        value={heading}
                        onChange={(e) => setHeading(e.target.value)}
                    />
                    <TextArea
                        placeholder="Zadajte popis"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    {selectedType === 'ANKETA' && (
                        <SurveyPostForm
                            options={surveyOptions}
                            onOptionsChange={setSurveyOptions}
                        />
                    )}
                    {selectedType !== 'ANKETA' && (
                        <>
                            {fileNames.length > 0 && <FileName>Súbory: {fileNames.join(', ')}</FileName>}
                            <FileInputLabel>
                                <FaUpload style={{ marginRight: '10px' }} />
                                Nahrať súbory
                                <FileInput type="file" multiple onChange={handleFileChange} />
                            </FileInputLabel>
                        </>
                    )}
                    {isUploading ? (
                        <div>
                            <SpinnerIcon /> Súbor sa nahráva...
                        </div>
                    ) : processing ? (
                        <div>
                            <SpinnerIcon /> Video sa spracúva, o chvíľu bude hotové...
                        </div>
                    ) : uploadSuccess ? (
                        <SuccessIcon />
                    ) : (processingFailed || longResponse) ? (
                        // Terminal state: only Reset (not Submit) so a retry can't create a duplicate post.
                        <ResetButton onClick={resetForm}>Resetovať</ResetButton>
                    ) : (
                        <>
                            <SubmitButton onClick={handleSubmit}>Pridať</SubmitButton>
                            <ResetButton onClick={resetForm}>Resetovať</ResetButton>
                        </>
                    )}
                    {processingFailed && (
                        <LongResponseMessage>
                            Nahrávanie zlyhalo. Skúste to znova (Resetovať).
                        </LongResponseMessage>
                    )}
                    {longResponse && (
                        <LongResponseMessage>
                            Spracovanie trvá dlhšie, dokončí sa na pozadí — budete informovaní emailom.
                        </LongResponseMessage>
                    )}
                </UploadContainer>
            )}
        </Container>
    );
};

export default UploadSection;